import { CircleAlert, CircleCheck, Info } from 'lucide-react';
import { useEffect, useState, useSyncExternalStore } from 'react';
import { cn } from '../lib/cn';

export type ToastType = 'success' | 'error' | 'info';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastShowOptions {
  type: ToastType;
  message: string;
  /** 자동 닫힘까지의 ms (기본 3000, 0 이하면 수동 닫기 전용) */
  duration?: number;
  action?: ToastAction;
}

export interface ToastItem extends ToastShowOptions {
  id: number;
}

/* ---- 모듈 스코프 store (간단한 pub/sub) ---- */

type Listener = () => void;

const EMPTY: readonly ToastItem[] = [];

let nextId = 1;
let toastList: readonly ToastItem[] = EMPTY;
const listeners = new Set<Listener>();
const timers = new Map<number, ReturnType<typeof setTimeout>>();

function emit(): void {
  for (const listener of listeners) listener();
}

function show(options: ToastShowOptions): number {
  const id = nextId++;
  const item: ToastItem = { ...options, id };
  toastList = [...toastList, item];
  const duration = options.duration ?? 3000;
  if (Number.isFinite(duration) && duration > 0) {
    timers.set(
      id,
      setTimeout(() => dismiss(id), duration),
    );
  }
  emit();
  return id;
}

function dismiss(id: number): void {
  const timer = timers.get(id);
  if (timer !== undefined) {
    clearTimeout(timer);
    timers.delete(id);
  }
  toastList = toastList.filter((item) => item.id !== id);
  emit();
}

export const toast = { show, dismiss };

function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): readonly ToastItem[] {
  return toastList;
}

function getServerSnapshot(): readonly ToastItem[] {
  return EMPTY;
}

/* ---- 렌더링 ---- */

function ToastIcon({ type }: { type: ToastType }) {
  if (type === 'success') {
    return <CircleCheck className="size-5 shrink-0 text-success-500" aria-hidden="true" />;
  }
  if (type === 'error') {
    return <CircleAlert className="size-5 shrink-0 text-danger-500" aria-hidden="true" />;
  }
  return <Info className="size-5 shrink-0 text-info-500" aria-hidden="true" />;
}

function ToastView({ item }: { item: ToastItem }) {
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    const frame = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(frame);
  }, []);

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        'pointer-events-auto flex w-full max-w-sm items-center gap-2 rounded-md bg-inverse px-4 py-3 text-body text-fg-inverse shadow-raised',
        'transition-all duration-200 ease-out',
        entered ? 'translate-y-0 opacity-100' : '-translate-y-4 opacity-0',
      )}
    >
      <ToastIcon type={item.type} />
      <p className="min-w-0 flex-1">{item.message}</p>
      {item.action !== undefined ? (
        <button
          type="button"
          onClick={() => {
            item.action?.onClick();
            dismiss(item.id);
          }}
          className={cn(
            '-my-2 flex min-h-11 shrink-0 items-center px-2 text-body font-semibold text-gold-500',
            'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-600',
          )}
        >
          {item.action.label}
        </button>
      ) : null}
    </div>
  );
}

export interface ToasterProps {
  className?: string;
}

export function Toaster({ className }: ToasterProps) {
  const items = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  return (
    <div
      className={cn(
        'pointer-events-none fixed inset-x-0 top-0 z-50 flex flex-col items-center gap-2 px-4 pt-[calc(var(--safe-top)+0.5rem)]',
        className,
      )}
    >
      {items.map((item) => (
        <ToastView key={item.id} item={item} />
      ))}
    </div>
  );
}
